import React, { useContext } from 'react';
import '../../common/datatables/ReactTableStyle.css';
import { Row as TRow, Col, Alert } from 'react-bootstrap';
import { renderOverlay } from '../../common/listUtils';
import ConfigContext from '../../../contexts/ConfigContext';
import { AmStatusType, ApproverRoutingStatusType } from '../../../utils/Constants';
import { getActionMemo } from '../../../api/actionMemoApi';
import useAsyncWithState from '../../../hooks/useAsyncWithState';
import { ActionMemo } from '../../../api/actionMemoApiTypes';
import { formatCurrency, formatNameTitle, formatDate } from '../../../utils/displayUtils';
import Spinner from '../../common/Spinner';
import { StatusChangeRequestItem } from '../../../api/statusChangeRequestTypes';
import { getProjectBalance } from '../../../api/projectBalanceApi';
import { ProjectBalance } from '../../../api/projectBalanceApiTypes';

export interface RowType {
    original: StatusChangeRequestItem;
}

const SubRowAsync = ({ row }: { row: RowType }) => {
    const config = useContext(ConfigContext);

    const { value, loading, error } = useAsyncWithState(
        () => getActionMemo(config, row.original.id, false),
        {} as ActionMemo,
    );
    const {
        value: balance,
        loading: balanceLoading,
        error: balanceError,
    } = useAsyncWithState(
        () => value && value.id && getProjectBalance(config, value.id, false),
        null as ProjectBalance | null,
        [value],
    );

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    const contractSigner =
        value.approvers && value.approvers.find((approver) => approver.origin === 'SIGNER');
    const currentApprover =
        (value.amStatus === AmStatusType.Pending || value.amStatus === AmStatusType.Hold) &&
        value.approvers.find(
            (approver) => approver.routingStatus === ApproverRoutingStatusType.CURRENT,
        );

    return loading || balanceLoading ? (
        <Spinner />
    ) : (
        <div className="row am-list-properties mobile-hide">
            <div className="col">
                <TRow>
                    <Col sm="5">Creator </Col>
                    <Col sm="7" className="border-bottom p-0">
                        {value.amCreatorName}
                    </Col>
                </TRow>
                <TRow>
                    <Col sm="5">Project Manager </Col>
                    <Col sm="7" className="border-bottom p-0">
                        {value.projectManagerName}
                    </Col>
                </TRow>
                <TRow>
                    <Col sm="5">Created On</Col>
                    <Col sm="7" className="border-bottom p-0">
                        {formatDate(value.creationDate)}
                    </Col>
                </TRow>
                {value.approvalDate && (
                    <TRow>
                        <Col sm="5">Approved On</Col>
                        <Col sm="7" className="border-bottom p-0">
                            {formatDate(value.approvalDate)}
                        </Col>
                    </TRow>
                )}
                {value.dueDate && (
                    <TRow>
                        <Col sm="5">
                            Due On{' '}
                            {renderOverlay(
                                'The date your AM approval will expire and escalate to the next approver',
                            )}
                        </Col>
                        <Col sm="7" className="border-bottom p-0">
                            {value.dueDate && formatDate(value.dueDate)}
                        </Col>
                    </TRow>
                )}
                <TRow>
                    <Col sm="5">Completion Date</Col>
                    <Col sm="7" className="border-bottom p-0">
                        {formatDate(value.completionDate)}
                    </Col>
                </TRow>
                {value.statusChangeRequest && (
                    <TRow>
                        <Col sm="5">Requested By</Col>
                        <Col sm="7" className="border-bottom p-0">
                            {value.statusChangeRequest.requestedByName}
                        </Col>
                    </TRow>
                )}
            </div>
            <div className="col">
                <TRow>
                    <Col sm="4">Confidential </Col>
                    <Col sm="8" className="border-bottom p-0">
                        {value.confidential ? (
                            <span
                                className={`badge ${
                                    value.confidential
                                        ? ' text-dark badge-warning '
                                        : ' text-white badge-success'
                                }`}
                            >
                                YES
                            </span>
                        ) : (
                            'No'
                        )}
                    </Col>
                </TRow>
                {currentApprover && (
                    <TRow>
                        <Col sm="4">Current Approver</Col>
                        <Col sm="8" className="border-bottom p-0">
                            {formatNameTitle(
                                currentApprover.approverName,
                                currentApprover.approverTitle,
                            )}
                        </Col>
                    </TRow>
                )}
                {contractSigner && (
                    <>
                        {value.contractOrganizationName && (
                            <TRow>
                                <Col sm="4">Contract Entity</Col>
                                <Col sm="8" className="border-bottom p-0">
                                    {value.contractOrganizationName}
                                </Col>
                            </TRow>
                        )}
                        <TRow>
                            <Col sm="4">Contract Signer</Col>
                            <Col sm="8" className="border-bottom p-0">
                                {formatNameTitle(
                                    contractSigner.approverName,
                                    contractSigner.approverTitle,
                                )}
                            </Col>
                        </TRow>
                    </>
                )}
                <TRow>
                    <Col sm="4">Organization</Col>
                    <Col sm="8" className="border-bottom p-0">
                        {value.organizationName}
                    </Col>
                </TRow>
                <TRow>
                    <Col sm="4">Template Code</Col>
                    <Col sm="8" className="border-bottom p-0">
                        {value.templateCode}
                    </Col>
                </TRow>
                <TRow>
                    <Col sm="4">Description</Col>
                    <Col sm="8" className="border-bottom p-0">
                        {value.templateDescription}
                    </Col>
                </TRow>
            </div>
            <div className="col ml-3">
                {balanceError ? (
                    <Alert variant="info">{balanceError}</Alert>
                ) : (
                    <>
                        <TRow className="pr-3">
                            <Col sm="5">Budget + All Supplements </Col>
                            <Col sm="7" className="border-bottom p-0 text-right">
                                {balance && formatCurrency(balance.originalAmount)}
                            </Col>
                        </TRow>
                        <TRow className="pr-3">
                            <Col sm="5">Pending Commitments </Col>
                            <Col sm="7" className="border-bottom p-0 text-right">
                                {balance && formatCurrency(balance.fundsCheckTotal)}
                            </Col>
                        </TRow>
                        <TRow className="pr-3">
                            <Col sm="5">Commitments </Col>
                            <Col sm="7" className="border-bottom p-0 text-right">
                                {balance && formatCurrency(balance.commitmentTotal)}
                            </Col>
                        </TRow>
                        <TRow className="pr-3">
                            <Col sm="5">Actuals </Col>
                            <Col sm="7" className="border-bottom p-0 text-right">
                                {balance && formatCurrency(balance.actualTotal)}
                            </Col>
                        </TRow>
                        <TRow className="border pr-3 py-3 mt-3">
                            <Col sm="5">
                                <h3 className="responsive-total">Net Available </h3>
                            </Col>
                            <Col sm="7" className="p-0 text-right">
                                <h3 className="responsive-total">
                                    {balance &&
                                        formatCurrency(
                                            balance
                                                ? balance.originalAmount -
                                                      balance.fundsCheckTotal -
                                                      balance.commitmentTotal -
                                                      balance.actualTotal
                                                : 0,
                                        )}
                                </h3>
                            </Col>
                        </TRow>
                    </>
                )}
            </div>
        </div>
    );
};

export const generateStatusChangeRequestSubRow = () =>
    React.useCallback(({ row }: { row: RowType }) => <SubRowAsync row={row} />, []);
